import { useMemo, useState, useRef, type CSSProperties } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ClearOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  EditOutlined,
  LineChartOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../api/client';
import {
  adminAlertStyle,
  adminContentCardStyle,
  adminFilterCardStyle,
  adminPageWrap,
} from '../styles/adminPageShell';

const { Text, Title } = Typography;

/** 默认 6 个月窗口（与 HighPotential.starWindow 一致） */
const DEFAULT_MONTHS = ['2025-11', '2025-12', '2026-01', '2026-02', '2026-03', '2026-04'];

type EmployeeBrief = {
  id: number;
  empNo: string;
  name: string;
  position?: string | null;
  deptPath?: string | null;
  talentPool?: string | null;
  segment?: string | null;
};

type MonthCell = {
  value: number;
  unit?: string | null;
  remark?: string | null;
  updatedAt?: string;
} | null;

type PerfBucket = {
  score: number;
  level: 'H' | 'M' | 'L';
  source: string;
} | null;

type MatrixRow = {
  emp: EmployeeBrief;
  cells: Record<string, MonthCell>;
  avg: number | null;
  total: number | null;
  filledMonths: number;
  performance: PerfBucket;
};

type MatrixResp = {
  months: string[];
  metric: string;
  rows: MatrixRow[];
  batch: { id: number; name: string; phase: string } | null;
};

type MonthlyImportCandidate = {
  empId: number;
  empNo: string;
  name: string;
  position: string | null;
  deptPath: string | null;
  status: string | null;
};

type MonthlyImportPendingItem = {
  rowNo: number;
  name: string;
  candidates: MonthlyImportCandidate[];
  row: {
    name?: string;
    empNo?: string;
    yearMonth: string;
    metric?: string;
    value: number;
    unit?: string;
    remark?: string;
  };
};

const SEGMENT_OPTIONS = [
  { label: '销售端', value: '销售端' },
  { label: '医疗与营运端', value: '医疗与营运端' },
  { label: '营销获客端', value: '营销获客端' },
  { label: '后端（职能/战略/技术）', value: '后端（职能/战略/技术）' },
  { label: '高管经营班子', value: '高管经营班子' },
];

const filterLabelStyle: CSSProperties = {
  fontSize: 12,
  color: '#8c8c8c',
  marginBottom: 4,
  lineHeight: 1,
};

const LEVEL_META: Record<'H' | 'M' | 'L', { color: string; label: string }> = {
  H: { color: 'green', label: '高' },
  M: { color: 'blue', label: '中' },
  L: { color: 'red', label: '低' },
};

const shortDept = (path?: string | null) => {
  if (!path) return '-';
  const parts = path.split('/').filter(Boolean);
  return parts.length <= 2 ? path : `${parts[0]}/.../${parts[parts.length - 1]}`;
};

export function MonthlyPerformancePage() {
  const qc = useQueryClient();
  const [names, setNames] = useState<string[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [depts, setDepts] = useState<string[]>([]);
  const [metric, setMetric] = useState<string>('');
  const [segments, setSegments] = useState<string[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [editing, setEditing] = useState<
    | { emp: EmployeeBrief; ym: string; cell: MonthCell }
    | null
  >(null);
  const [editValue, setEditValue] = useState<number | null>(null);
  const [editUnit, setEditUnit] = useState<string>('');
  const [editRemark, setEditRemark] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<MonthlyImportPendingItem[]>([]);
  const [pendingSelection, setPendingSelection] = useState<Record<string, number | undefined>>({});

  const { data, isFetching, refetch } = useQuery<MatrixResp>({
    queryKey: ['monthly-perf', metric],
    queryFn: () =>
      api.get('/admin/monthly-performance/matrix', {
        params: {
          // PR-2：不再硬编码月份；后端默认读「当前批次」的盘点区间（performancePeriodMonths）
          metric: metric || undefined,
        },
      }) as unknown as Promise<MatrixResp>,
  });

  const months = data?.months ?? DEFAULT_MONTHS;
  const allRows = data?.rows ?? [];

  /** 从全量数据派生 6 个筛选下拉的选项 */
  const filterOptions = useMemo(() => {
    const segSet = new Set<string>();
    const nameSet = new Set<string>();
    const posSet = new Set<string>();
    const deptSet = new Set<string>();
    for (const r of allRows) {
      if (r.emp.segment) segSet.add(r.emp.segment);
      if (r.emp.name) nameSet.add(r.emp.name);
      if (r.emp.position) posSet.add(r.emp.position);
      if (r.emp.deptPath) deptSet.add(r.emp.deptPath);
    }
    const toOpt = (s: Set<string>) =>
      Array.from(s)
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ label: v, value: v }));
    return {
      segments: toOpt(segSet),
      names: toOpt(nameSet),
      positions: toOpt(posSet),
      depts: toOpt(deptSet),
    };
  }, [allRows]);

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (segments.length && !(r.emp.segment && segments.includes(r.emp.segment))) return false;
      if (levels.length) {
        const lv = r.performance?.level;
        const matched =
          (lv && levels.includes(lv)) || (levels.includes('NONE') && !r.performance);
        if (!matched) return false;
      }
      if (names.length && !names.includes(r.emp.name)) return false;
      if (positions.length && !(r.emp.position && positions.includes(r.emp.position))) return false;
      if (depts.length && !(r.emp.deptPath && depts.includes(r.emp.deptPath))) return false;
      return true;
    });
  }, [allRows, segments, levels, names, positions, depts]);

  const stats = useMemo(() => {
    const empCount = rows.length;
    const fullCount = rows.filter((r) => r.filledMonths === months.length).length;
    const partial = rows.filter((r) => r.filledMonths > 0 && r.filledMonths < months.length).length;
    const empty = rows.filter((r) => r.filledMonths === 0).length;
    const levelCounts = { H: 0, M: 0, L: 0 } as Record<'H' | 'M' | 'L', number>;
    for (const r of rows) {
      if (r.performance) levelCounts[r.performance.level]++;
    }
    return { empCount, fullCount, partial, empty, levelCounts };
  }, [rows, months.length]);

  const upsertMut = useMutation({
    mutationFn: (payload: {
      empId: number;
      yearMonth: string;
      metric?: string;
      value: number;
      unit?: string;
      remark?: string;
    }) => api.put('/admin/monthly-performance', payload),
    onSuccess: () => {
      message.success('已保存');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['monthly-perf'] });
    },
  });

  const importMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await http.post('/admin/monthly-performance/import-excel', fd, {
        params: {
          metric: metric || undefined,
          source: `Excel-${new Date().toISOString().slice(0, 10)}`,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data?.data ?? res.data;
    },
    onSuccess: (resp: any) => {
      const success = resp?.success ?? 0;
      const skipped = resp?.skipped ?? 0;
      const errors: string[] = resp?.errors ?? [];
      const pending: MonthlyImportPendingItem[] = Array.isArray(resp?.pending) ? resp.pending : [];
      Modal.info({
        title: '导入完成',
        width: 560,
        content: (
          <div>
            <p>
              成功写入 <b>{success}</b> 条 · 跳过 <b>{skipped}</b> 条 · 错误{' '}
              <b style={{ color: errors.length ? '#ff4d4f' : undefined }}>{errors.length}</b> 条 · 同名待确认{' '}
              <b style={{ color: pending.length ? '#1677ff' : undefined }}>{pending.length}</b> 条
            </p>
            {errors.length > 0 && (
              <div
                style={{
                  maxHeight: 220,
                  overflow: 'auto',
                  background: '#fafafa',
                  padding: 8,
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                {errors.slice(0, 50).map((e, i) => (
                  <div key={i}>· {e}</div>
                ))}
                {errors.length > 50 && <div>... 共 {errors.length} 条</div>}
              </div>
            )}
          </div>
        ),
      });
      if (pending.length > 0) {
        const initSel: Record<string, number | undefined> = {};
        pending.forEach((item) => {
          if (item.candidates.length === 1) initSel[`row-${item.rowNo}`] = item.candidates[0].empId;
        });
        setPendingItems(pending);
        setPendingSelection(initSel);
        setPendingOpen(true);
      }
      qc.invalidateQueries({ queryKey: ['monthly-perf'] });
    },
  });

  const resolveImportMut = useMutation({
    mutationFn: (items: Array<{ rowNo: number; empId?: number; row: MonthlyImportPendingItem['row'] }>) =>
      api.post('/admin/monthly-performance/import-excel/resolve', {
        items,
        source: `Excel-${new Date().toISOString().slice(0, 10)}`,
      }),
    onSuccess: (resp: any, vars) => {
      const sentRowNos = new Set(vars.map((x) => x.rowNo));
      const nextPendingFromApi: MonthlyImportPendingItem[] = Array.isArray(resp?.pending)
        ? resp.pending
        : [];
      const pendingRowNos = new Set(nextPendingFromApi.map((p) => p.rowNo));
      setPendingItems((prev) => {
        const keepOld = prev.filter((p) => !sentRowNos.has(p.rowNo) || pendingRowNos.has(p.rowNo));
        const merged = [...keepOld.filter((p) => !pendingRowNos.has(p.rowNo)), ...nextPendingFromApi];
        if (merged.length === 0) setPendingOpen(false);
        return merged;
      });
      message.success(
        `已确认 ${resp?.success ?? 0} 条` +
          (resp?.errors?.length ? `，失败 ${resp.errors.length} 条` : '') +
          (resp?.pending?.length ? `，仍待确认 ${resp.pending.length} 条` : ''),
      );
      qc.invalidateQueries({ queryKey: ['monthly-perf'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '待确认处理失败');
    },
  });

  const onPickFile = () => fileInputRef.current?.click();
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    importMut.mutate(f);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    // 生成宽表模板：工号 | 姓名 | 月份列…
    const header = ['姓名', '工号', ...months].join(',');
    const sample = ['E0001', '示例员工', ...months.map(() => '')].join(',');
    const csv = `\uFEFF${header}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `绩效业绩-导入模板-${months[0]}_${months[months.length - 1]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openEdit = (emp: EmployeeBrief, ym: string, cell: MonthCell) => {
    setEditing({ emp, ym, cell });
    setEditValue(cell?.value ?? null);
    setEditUnit(cell?.unit ?? '');
    setEditRemark(cell?.remark ?? '');
  };
  const saveEdit = () => {
    if (!editing) return;
    if (editValue == null || Number.isNaN(editValue)) {
      message.warning('请填入业绩值');
      return;
    }
    upsertMut.mutate({
      empId: editing.emp.id,
      yearMonth: editing.ym,
      metric: metric || '',
      value: editValue,
      unit: editUnit || undefined,
      remark: editRemark || undefined,
    });
  };

  const resolveOnePending = (item: MonthlyImportPendingItem) => {
    const key = `row-${item.rowNo}`;
    const empId = pendingSelection[key];
    if (!empId) {
      message.warning(`请先为第 ${item.rowNo} 行选择员工`);
      return;
    }
    resolveImportMut.mutate([{ rowNo: item.rowNo, empId, row: item.row }]);
  };

  const resolveSelectedPending = () => {
    const items = pendingItems
      .map((item) => ({
        rowNo: item.rowNo,
        empId: pendingSelection[`row-${item.rowNo}`],
        row: item.row,
      }))
      .filter((x) => !!x.empId) as Array<{
      rowNo: number;
      empId: number;
      row: MonthlyImportPendingItem['row'];
    }>;
    if (!items.length) {
      message.warning('请先选择至少 1 条待确认记录');
      return;
    }
    resolveImportMut.mutate(items);
  };

  const columns: ColumnsType<MatrixRow> = useMemo(() => {
    const monthCols: ColumnsType<MatrixRow> = months.map((ym) => ({
      title: ym.replace('-', '·'),
      key: ym,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, row: MatrixRow) => {
        const c = row.cells[ym];
        if (!c) {
          return (
            <Tooltip title="尚无数据，点击填写">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                style={{ color: '#bfbfbf' }}
                onClick={() => openEdit(row.emp, ym, null)}
              >
                —
              </Button>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={
              <div>
                <div>值：{c.value}{c.unit ? ` ${c.unit}` : ''}</div>
                {c.remark && <div>备注：{c.remark}</div>}
                {c.updatedAt && (
                  <div style={{ fontSize: 11, color: '#bbb' }}>
                    更新：{new Date(c.updatedAt).toLocaleString()}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#bbb' }}>点击编辑</div>
              </div>
            }
          >
            <Button
              type="text"
              size="small"
              onClick={() => openEdit(row.emp, ym, c)}
              style={{ padding: '0 4px', fontVariantNumeric: 'tabular-nums' }}
            >
              <span style={{ fontWeight: 600, color: '#262626' }}>{c.value}</span>
              {c.unit && <span style={{ color: '#8c8c8c', marginLeft: 2 }}>{c.unit}</span>}
            </Button>
          </Tooltip>
        );
      },
    }));

    return [
      {
        title: '员工',
        key: 'emp',
        fixed: 'left' as const,
        width: 240,
        render: (_: unknown, r: MatrixRow) => (
          <div style={{ lineHeight: 1.35 }}>
            <Space size={6} wrap={false}>
              <span style={{ fontWeight: 600 }}>{r.emp.name}</span>
              {r.emp.segment ? (
                <Tag color="purple" style={{ marginInlineStart: 0 }}>
                  {r.emp.segment}
                </Tag>
              ) : null}
            </Space>
            <div
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.emp.deptPath || ''}
            >
              {r.emp.position || '-'} · {shortDept(r.emp.deptPath)}
            </div>
          </div>
        ),
      },
      ...monthCols,
      {
        title: '已填月数',
        key: 'filled',
        width: 100,
        align: 'center' as const,
        sorter: (a: MatrixRow, b: MatrixRow) => a.filledMonths - b.filledMonths,
        render: (_: unknown, r: MatrixRow) => {
          const color =
            r.filledMonths === months.length
              ? 'success'
              : r.filledMonths === 0
                ? 'red'
                : 'orange';
          return (
            <Tag color={color} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {r.filledMonths} / {months.length}
            </Tag>
          );
        },
      },
      {
        title: '平均',
        key: 'avg',
        width: 100,
        align: 'right' as const,
        sorter: (a: MatrixRow, b: MatrixRow) => (a.avg ?? -Infinity) - (b.avg ?? -Infinity),
        render: (_: unknown, r: MatrixRow) =>
          r.avg == null ? (
            <Text type="secondary">—</Text>
          ) : (
            <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.avg}</span>
          ),
      },
      {
        title: (
          <Tooltip title="基于该员工所在「端」的全员均值降序，按 30%/50%/20% 切档为高/中/低。来源：PerformanceScore（当前批次）。">
            业绩等级
          </Tooltip>
        ),
        key: 'level',
        width: 130,
        align: 'center' as const,
        filters: [
          { text: '高', value: 'H' },
          { text: '中', value: 'M' },
          { text: '低', value: 'L' },
          { text: '未切档', value: '' },
        ],
        onFilter: (v: any, r: MatrixRow) =>
          v === '' ? !r.performance : r.performance?.level === v,
        sorter: (a: MatrixRow, b: MatrixRow) => {
          const order = { H: 0, M: 1, L: 2 } as Record<string, number>;
          const ax = a.performance ? order[a.performance.level] : 9;
          const bx = b.performance ? order[b.performance.level] : 9;
          return ax - bx;
        },
        render: (_: unknown, r: MatrixRow) => {
          if (!r.performance) {
            return <Text type="secondary">—</Text>;
          }
          const meta = LEVEL_META[r.performance.level];
          return (
            <Tooltip
              title={
                <div>
                  <div>端均值：{r.performance.score}</div>
                  <div style={{ fontSize: 11, color: '#bbb' }}>来源：{r.performance.source}</div>
                </div>
              }
            >
              <Tag color={meta.color} style={{ marginInlineStart: 0 }}>
                {meta.label}
              </Tag>
              <div
                style={{
                  fontSize: 11,
                  color: '#8c8c8c',
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}
              >
                {r.performance.score.toFixed(2)}
              </div>
            </Tooltip>
          );
        },
      },
    ];
  }, [months, metric]);

  return (
    <div style={adminPageWrap}>
      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message={
          <Space size={8}>
            <LineChartOutlined />
            <span style={{ fontWeight: 600 }}>绩效业绩 · 月度数据中心</span>
          </Space>
        }
        description={
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            维护每位员工 <Text strong>2025-11 ~ 2026-04</Text> 共 6
            个月的业绩值，作为九宫格 H/M/L 与高潜识别的事实依据。支持 Excel
            导入（宽表：姓名·工号（选填）·月份列；或长表：姓名·yearMonth·value）与单元格点击修改。同名歧义会进入待确认列表手动选人。
            指标可按需切换（如「销售额 / 完成率 / 接诊量」），<Text strong>留空表示综合业绩值</Text>。
            <br />
            <Text strong>绩效切档</Text>：按业务条线分 5 个端（销售端 / 医疗与营运端 / 营销获客端 / 后端 / 高管经营班子），
            在端内按月度均值降序，<Text strong>30%/50%/20%</Text> 划分为高 / 中 / 低；
            数据来自 HR 提供的「绩效业绩.csv」，已写入 PerformanceScore（当前批次：
            {data?.batch ? <Tag color="purple">{data.batch.name}</Tag> : '—'}）。
          </div>
        }
      />

      <Card style={adminFilterCardStyle} bodyStyle={{ padding: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="0 0 auto">
            <Statistic
              title="员工数"
              value={stats.empCount}
              prefix={<TeamOutlined />}
              valueStyle={{ fontSize: 20 }}
            />
          </Col>
          <Col flex="0 0 auto">
            <Statistic
              title="完整 6 月"
              value={stats.fullCount}
              valueStyle={{ fontSize: 20, color: '#52c41a' }}
            />
          </Col>
          <Col flex="0 0 auto">
            <Statistic
              title="部分填写"
              value={stats.partial}
              valueStyle={{ fontSize: 20, color: '#faad14' }}
            />
          </Col>
          <Col flex="0 0 auto">
            <Statistic
              title="尚未填写"
              value={stats.empty}
              valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
            />
          </Col>
          <Col flex="0 0 auto">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>绩效切档（端内 30/50/20）</span>
              <Space size={6}>
                <Tag color="green">高 {stats.levelCounts.H}</Tag>
                <Tag color="blue">中 {stats.levelCounts.M}</Tag>
                <Tag color="red">低 {stats.levelCounts.L}</Tag>
              </Space>
            </div>
          </Col>
          <Col flex="1 1 auto" />
          <Col>
            <Space size={8}>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                刷新
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setNames([]);
                  setPositions([]);
                  setDepts([]);
                  setMetric('');
                  setSegments([]);
                  setLevels([]);
                }}
              >
                清空筛选
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        size="small"
        style={{ ...adminFilterCardStyle, marginTop: 12 }}
        bodyStyle={{ padding: 16 }}
      >
        <Row gutter={[16, 12]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={5}>
            <div style={filterLabelStyle}>端</div>
            <Select
              mode="multiple"
              placeholder="选择端（可多选）"
              allowClear
              showSearch
              maxTagCount="responsive"
              style={{ width: '100%' }}
              value={segments}
              onChange={(v) => setSegments(v)}
              options={filterOptions.segments.length ? filterOptions.segments : SEGMENT_OPTIONS}
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <div style={filterLabelStyle}>业绩等级</div>
            <Select
              mode="multiple"
              placeholder="高 / 中 / 低"
              allowClear
              showSearch
              maxTagCount="responsive"
              style={{ width: '100%' }}
              value={levels}
              onChange={(v) => setLevels(v)}
              options={[
                { label: '高', value: 'H' },
                { label: '中', value: 'M' },
                { label: '低', value: 'L' },
                { label: '未切档', value: 'NONE' },
              ]}
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div style={filterLabelStyle}>姓名</div>
            <Select
              mode="multiple"
              placeholder="选择姓名（可多选/搜索）"
              allowClear
              showSearch
              maxTagCount="responsive"
              style={{ width: '100%' }}
              value={names}
              onChange={(v) => setNames(v)}
              options={filterOptions.names}
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div style={filterLabelStyle}>岗位</div>
            <Select
              mode="multiple"
              placeholder="选择岗位（可多选/搜索）"
              allowClear
              showSearch
              maxTagCount="responsive"
              style={{ width: '100%' }}
              value={positions}
              onChange={(v) => setPositions(v)}
              options={filterOptions.positions}
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div style={filterLabelStyle}>部门</div>
            <Select
              mode="multiple"
              placeholder="选择部门（可多选/搜索）"
              allowClear
              showSearch
              maxTagCount="responsive"
              style={{ width: '100%' }}
              value={depts}
              onChange={(v) => setDepts(v)}
              options={filterOptions.depts.map((o) => ({
                label: o.label,
                value: o.value,
                title: o.label,
              }))}
              optionFilterProp="label"
            />
          </Col>
        </Row>
      </Card>

      <Card
        style={adminContentCardStyle}
        bodyStyle={{ padding: 16 }}
        title={
          <Space size={8}>
            <Title level={5} style={{ margin: 0 }}>
              员工 × 月份 业绩矩阵
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              （点击单元格直接编辑）
            </Text>
          </Space>
        }
        extra={
          <Space>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={onFileChange}
            />
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载 CSV 模板
            </Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              loading={importMut.isPending}
              onClick={onPickFile}
            >
              导入 Excel
            </Button>
          </Space>
        }
      >
        {rows.length === 0 && !isFetching ? (
          <Empty description="暂无员工数据" />
        ) : (
          <Table<MatrixRow>
            rowKey={(r) => r.emp.id}
            size="small"
            loading={isFetching}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 30, showSizeChanger: true, pageSizeOptions: [20, 30, 50, 100] }}
            scroll={{ x: 240 + 110 * months.length + 340 }}
            sticky
          />
        )}
      </Card>

      <Modal
        title={`同名待确认（${pendingItems.length}）`}
        open={pendingOpen}
        onCancel={() => setPendingOpen(false)}
        width={820}
        footer={[
          <Button key="cancel" onClick={() => setPendingOpen(false)}>
            关闭
          </Button>,
          <Button
            key="batch"
            type="primary"
            loading={resolveImportMut.isPending}
            onClick={resolveSelectedPending}
          >
            批量确认已选择
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="刷新或离开页面将丢失待确认列表，可重新导入同一文件继续处理。"
        />
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          {pendingItems.map((item, idx) => {
            const key = `row-${item.rowNo}`;
            return (
              <div
                key={key}
                style={{
                  paddingBottom: 10,
                  borderBottom: idx < pendingItems.length - 1 ? '1px dashed #e5e7eb' : undefined,
                }}
              >
                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Typography.Text>
                    第 {item.rowNo} 行 · {item.name} · {item.row.yearMonth}
                    {item.row.metric ? ` · ${item.row.metric}` : ' · 综合'}
                  </Typography.Text>
                  <Space>
                    <Select
                      size="small"
                      style={{ minWidth: 420 }}
                      placeholder="选择员工"
                      value={pendingSelection[key]}
                      onChange={(v) => setPendingSelection((prev) => ({ ...prev, [key]: v }))}
                      options={item.candidates.map((c) => ({
                        value: c.empId,
                        label: `${c.empNo} · ${c.name} · ${c.position || '—'} · ${c.deptPath || '—'} · ${c.status || '—'}`,
                      }))}
                    />
                    <Button
                      size="small"
                      type="primary"
                      loading={resolveImportMut.isPending}
                      onClick={() => resolveOnePending(item)}
                    >
                      确认
                    </Button>
                  </Space>
                </Space>
              </div>
            );
          })}
          {pendingItems.length === 0 && <Text type="secondary">暂无待确认记录</Text>}
        </Space>
      </Modal>

      <Modal
        open={!!editing}
        title={
          editing
            ? `${editing.emp.name} · ${editing.ym}${metric ? ` · ${metric}` : ''}`
            : '编辑业绩'
        }
        onCancel={() => setEditing(null)}
        onOk={saveEdit}
        confirmLoading={upsertMut.isPending}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        {editing && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">员工：</Text>
              <Text strong>{editing.emp.name}</Text>
              <br />
              <Text type="secondary">岗位/部门：</Text>
              <Text>
                {editing.emp.position || '-'} · {shortDept(editing.emp.deptPath)}
              </Text>
            </div>
            <div>
              <Text>业绩值</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                value={editValue ?? undefined}
                onChange={(v) => setEditValue(typeof v === 'number' ? v : null)}
                placeholder="请输入数值"
                step={0.01}
              />
            </div>
            <div>
              <Text>单位（可选）</Text>
              <Input
                style={{ marginTop: 4 }}
                value={editUnit}
                onChange={(e) => setEditUnit(e.target.value)}
                placeholder="如：元 / % / 单"
              />
            </div>
            <div>
              <Text>备注（可选）</Text>
              <Input.TextArea
                style={{ marginTop: 4 }}
                value={editRemark}
                onChange={(e) => setEditRemark(e.target.value)}
                rows={2}
                placeholder="口径说明 / 异常解释"
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}
